import { CheckActivationResponse, HttpError } from '@eo-storefronts/eo-core'
import { useState } from 'react'
import { useEoValue } from '~/src/hooks/useEoState'
import { checkActivation } from '~/src/services/CustomerService'
import { AUTHENTICATION_STATE, AuthenticationState } from '~/src/stores/authentication'
import useSetAuthState from '~/src/hooks/auth/useSetAuthState'
import useAuthenticationFlowDialogs from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import useFetchCustomer from '~/src/hooks/customer/useFetchCustomer'
import useSetCustomerState from '~/src/hooks/customer/useSetCustomerState'

interface ReturnsType {
  check(displayError: boolean): Promise<void>,
  activated: boolean,
  error: string | null,
}

const useCheckActivation = (): ReturnsType => {
  const authState = useEoValue(AUTHENTICATION_STATE)
  const [ error, setError ] = useState<string | null>(null)
  const [ activated, setActivated ] = useState<boolean>(false)
  const { setCustomer } = useSetCustomerState()
  const { setAuthState } = useSetAuthState()
  const { end } = useAuthenticationFlowDialogs()

  const _reset = () => {
    setActivated(false)
    setError(null)
  }

  const _doCheckActivation = async (displayError: boolean): Promise<void> => {
    _reset()

    if (!authState.customer?.id) {
      return
    }

    try {
      const response: CheckActivationResponse = await checkActivation(authState.customer.id)

      if (!response) {
        return
      }

      const newAuthState: AuthenticationState = {
        guest: undefined,
        customer: {
          id: authState.customer!.id,
          uid: authState.customer!.uid,
          token: response.customerToken
        },
        loggedInAs: 'customer'
      }

      setAuthState(newAuthState)
      setActivated(true)
      await setCustomer(newAuthState)
      end()

    } catch (error: any) {
      if (!displayError || !('message' in error)) {
        return
      }

      setError((error as HttpError).message)
    }
  }

  const check = async (displayError: boolean): Promise<void> => {
    if (displayError) {
      setError(null)
    }

    await _doCheckActivation(displayError)
  }

  return { check, activated, error }
}

export default useCheckActivation
